/* Alte Foto- und Logo-Dateien nach dem Speichern aufräumen — reine Funktionen.
 *
 * Beim Ersetzen eines Fotos oder Logos bleibt die alte Datei absichtlich
 * liegen (siehe playerPhoto.js; Logos gehören seit dem Klubregister dem Register, nicht mehr dem Spieler): Die Datenbank zeigt bis zum
 * Speichern-Knopf noch auf sie, und Verwerfen muss sie zurückbringen. Erst NACH
 * erfolgreichem Speichern sind die nicht mehr referenzierten Dateien entbehrlich.
 *
 * Wie die Liste entsteht (pathsToDelete):
 *   Kandidaten = was der gespeicherte Vorher-Stand referenziert hat
 *                (Foto: Original samt .thumb/.md; Logo: die Datei)
 *              + in dieser Seitensitzung hochgeladene, aber nie gespeicherte
 *                Zwischenstände (Foto A ersetzt durch Foto B vor dem Speichern)
 *   Behalten   = alles, was der gespeicherte Nachher-Stand ODER irgendein anderer
 *                Spieler noch referenziert (inkl. bioPhotoPath und dessen Fassungen)
 *                ODER was in `keep` steht: die Logo-Pfade der Transferhistorie dieses
 *                Spielers (player_transfers). Ein Logo, das nur dort noch genannt wird,
 *                darf nicht gelöscht werden, wenn das Logo des aktuellen Vereins
 *                ersetzt wird.
 *   Gelöscht   = Kandidaten minus Behalten, nur unter avatars/
 *
 * Nicht gelöscht wird bewusst: das Akten-Foto (bioPhotoPath, Altbestand). Es ist
 * kein Zwischenstand, sondern ein eigenes, früher extra hochgeladenes Bild; es
 * bleibt im Speicher (unter biophoto/) auffindbar, wie im CHANGELOG zugesagt.
 *
 * Gelöscht wird nur, wenn das Speichern gelungen ist (saveWithCleanup) und der
 * Nutzer löschen darf (admin, Speicherregel player_files_delete). Fehler beim
 * Löschen bleiben stumm: Die Datei liegt dann weiter da, nichts geht verloren.
 */

import { derivedCandidates, VARIANT_NAMES } from './photoVariants.js';

const SAFE_PREFIXES = ['avatars/'];

/** Alle Dateien eines Fotos (Original und Fassungen) bzw. Logos (nur die Datei). */
export function filesFor(kind, path) {
  if (!path) return [];
  if (kind === 'logo') return [path];
  /* Fassungen gibt es als webp, png (neu, mit Transparenz) und jpg (Altbestand) — alle gehören dazu */
  return [path, ...VARIANT_NAMES.flatMap((n) => derivedCandidates(path, n))];
}

/** Alles, was ein Spielerstand an Foto-/Logo-Dateien referenziert — nur zum Schützen, nie zum Löschen. */
export function referencedFiles(player) {
  const out = new Set();
  if (!player) return out;
  filesFor('photo', player.avatarPath).forEach((p) => out.add(p));
  filesFor('photo', player.bioPhotoPath).forEach((p) => out.add(p));
  filesFor('logo', player.currentClubLogoPath).forEach((p) => out.add(p));
  return out;
}

/** Löschkandidaten aus einem gespeicherten Stand: Spielerfoto und Vereinslogo, NICHT das Akten-Foto. */
export function candidatesOf(player) {
  if (!player) return [];
  return [
    ...filesFor('photo', player.avatarPath),
    ...filesFor('logo', player.currentClubLogoPath),
  ];
}

/**
 * Welche Dateien nach einem erfolgreichen Speichern gelöscht werden dürfen.
 * uploaded: [{ kind: 'photo' | 'logo', path }] aus dieser Sitzung; others: die
 * übrigen Spieler (deren Verweise werden geschützt).
 */
export function pathsToDelete({ before, after, uploaded = [], others = [], keep: extraKeep = [] }) {
  const keep = new Set(referencedFiles(after));
  for (const p of extraKeep) if (p) keep.add(p);
  for (const o of others) referencedFiles(o).forEach((p) => keep.add(p));

  const candidates = new Set(candidatesOf(before));
  for (const u of uploaded) filesFor(u.kind, u.path).forEach((p) => candidates.add(p));

  return [...candidates].filter(
    (p) => p && !keep.has(p) && SAFE_PREFIXES.some((prefix) => p.startsWith(prefix))
  );
}

/**
 * Alle Pfade sammeln, die nie gelöscht werden dürfen (Logos der Transferhistorie und des Klubregisters).
 * sources: Funktionen, die je eine Liste von Pfaden liefern (frisch aus der Datenbank). Schlägt EINE davon fehl,
 * ist das Ergebnis null — der Aufrufer löscht dann NICHTS (fail closed). Wirft nie.
 * @returns {Promise<string[]|null>}
 */
export async function collectProtectedPaths(sources) {
  try {
    const lists = await Promise.all((sources || []).map((fn) => fn()));
    return lists.flat().filter(Boolean);
  } catch (e) {
    console.error('[cleanup] Geschützte Pfade nicht lesbar, es wird nichts gelöscht:', e && e.message);
    return null;
  }
}

/**
 * Speichern und danach aufräumen. Schlägt save() fehl (wirft), wird NICHTS gelöscht.
 * Das Löschen läuft im Hintergrund und bremst das Speichern nicht: Rückgabe ist
 * { result, cleaned } — `cleaned` ist ein bereits gestartetes Promise (Anzahl
 * gelöschter Dateien), das nie wirft.
 *
 * Ohne Löschrecht (canDelete false) wird gar nicht erst versucht zu löschen: still, ohne Meldung.
 */
export async function saveWithCleanup({
  save, player, before, others = [], uploaded = [], keep = [], canDelete, remove,
}) {
  const result = await save(player);
  if (!canDelete) return { result, cleaned: Promise.resolve(0) };

  const paths = pathsToDelete({ before, after: result, uploaded, others, keep });
  const cleaned = (async () => {
    let n = 0;
    for (const path of paths) {
      try {
        await remove(path);
        n++;
      } catch {
        /* Datei bleibt liegen, kein Schaden */
      }
    }
    return n;
  })();
  return { result, cleaned };
}
